import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Oakland", cities: "Fruitvale, Rockridge, Temescal, Montclair, Grand Lake" },
    { zone: "Fremont", cities: "Centerville, Irvington, Warm Springs, Niles, Mission San Jose" },
    { zone: "Hayward", cities: "South Hayward, Castro Valley, San Lorenzo, Cherryland, Ashland" },
    { zone: "Tri-Valley", cities: "Dublin, Pleasanton, Livermore, San Ramon, Danville" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Áreas que Servimos en el Condado de Alameda</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en Oakland, Fremont, Hayward y más.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div
              key={i}
              className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en el Condado de Alameda</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const AlamedaSpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "alameda", name: "Condado de Alameda", type: "region", regionSlug: "alameda" }}>
      <SEO
        title="Limpieza del Hogar en Alameda, Oakland, Fremont & Hayward — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en Oakland, Fremont, Hayward, Berkeley y todo el Condado de Alameda. Precios planos, reserva en 60 segundos, garantía del 200%."
        keywords="limpieza del hogar condado de alameda, servicio de limpieza oakland, limpieza fremont ca, empleada doméstica hayward, maid service alameda county español, limpieza de casas berkeley, limpieza east bay, limpieza dublin pleasanton livermore"
        url="https://uplevelcleaning.com/es/alameda"
        locationName="Condado de Alameda"
        regionSlug="alameda"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "Condado de Alameda", url: "/es/alameda" }
        ]}
        reviews={[
          { author: "Gabriela R.", rating: 5, reviewBody: "Vivo en Fremont y buscaba un servicio de limpieza confiable en español. ¡Uplevel Cleaning es exactamente lo que necesitaba! Mi hogar quedó impecable.", datePublished: "2026-03-18" },
          { author: "Miguel S.", rating: 5, reviewBody: "Contraté la limpieza profunda para mi casa en Hayward. El equipo llegó puntual, fue muy detallista y el precio fue justo. ¡Totalmente recomendado!", datePublished: "2026-02-22" },
          { author: "Leticia M.", rating: 5, reviewBody: "Llevo 6 meses con el servicio quincenal en Oakland. Siempre quedan satisfechos con el trabajo. No busquen más — ¡Uplevel es el mejor!", datePublished: "2026-01-30" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="el Condado de Alameda" regionSlug="alameda" />

          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {[
                "Oakland · Fremont · Hayward",
                "Berkeley · Dublin · Pleasanton",
                "Garantía del 200%",
                "Precios Transparentes",
                "Reserva en 60 Segundos",
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <SpanishWhyUs cityName="el Condado de Alameda" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="oakland" />
          <Guarantee />
          <SpanishFAQ cityName="el Condado de Alameda" />
          <InternalLinks />
          <SpanishFinalCTA cityName="el Condado de Alameda" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default AlamedaSpanishPage;
